import { useState, useEffect } from "react";
import axios from "axios";
import { Star, Trash2, Cloud, Loader2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FavoritesList = ({ favorites, onSelect, onRemove, getFlightCategoryStyle }) => {
  const [weatherData, setWeatherData] = useState({});
  const [loadingWeather, setLoadingWeather] = useState({});

  useEffect(() => {
    // Fetch weather for all favorites
    favorites.forEach(fav => {
      fetchWeatherForFavorite(fav.icao);
    });
  }, [favorites]);

  const fetchWeatherForFavorite = async (icao) => {
    if (weatherData[icao] || loadingWeather[icao]) return;

    setLoadingWeather(prev => ({ ...prev, [icao]: true }));

    try {
      const response = await axios.get(`${API}/weather/metar/${icao}`);
      setWeatherData(prev => ({ ...prev, [icao]: response.data }));
    } catch (error) {
      setWeatherData(prev => ({ ...prev, [icao]: null }));
    } finally {
      setLoadingWeather(prev => ({ ...prev, [icao]: false }));
    }
  };

  if (favorites.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="inline-flex p-4 rounded-full bg-slate-100 mb-6">
          <Star className="w-12 h-12 text-slate-400" />
        </div>
        <h2 className="text-xl font-bold text-slate-700 mb-2">
          No Favorite Airports
        </h2>
        <p className="text-slate-500 max-w-md mx-auto">
          Search for an airport and click the star icon to add it to your favorites for quick access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Star className="w-5 h-5 text-yellow-500" />
          Favorite Airports
        </h2>
        <Badge variant="outline" className="border-slate-300 text-slate-600">
          {favorites.length} saved
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {favorites.map((fav) => {
          const weather = weatherData[fav.icao];
          const isLoading = loadingWeather[fav.icao];

          return (
            <div
              key={fav.id}
              className="weather-card p-4 card-hover cursor-pointer group"
              onClick={() => onSelect(fav.icao)}
              data-testid={`favorite-${fav.icao}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-yellow-100 border border-yellow-200">
                    <MapPin className="w-4 h-4 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800 font-mono">
                      {fav.icao}
                    </h3>
                    {fav.name && (
                      <p className="text-xs text-slate-500">{fav.name}</p>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-600 hover:bg-red-50 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(fav.icao);
                  }}
                  data-testid={`remove-favorite-${fav.icao}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {isLoading ? (
                <div className="flex items-center gap-2 text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Loading weather...</span>
                </div>
              ) : weather ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Cloud className="w-4 h-4 text-slate-400" />
                    <Badge className={`${getFlightCategoryStyle(weather.flight_category)} border text-xs`}>
                      {weather.flight_category}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 font-mono truncate">
                    {weather.raw?.substring(0, 50)}...
                  </p>
                </div>
              ) : (
                <p className="text-sm text-slate-500">Click to view weather</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FavoritesList;
